import type { ChangeEvent } from 'react'
import { CheckCircle2, XCircle } from 'lucide-react'
import type { ValidatePromoCode } from '@/modules/auth/use-cases'
import { promoAppliedMessage } from '@/modules/auth/ui/components'
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/primitives'
import type { OrgFormData, OrgType } from '../types'
import { useOrgFormStep, type OrgFormCountry } from './use-org-form-step'

export type OrgFormStepProps = {
  initialValue: OrgFormData
  orgType: OrgType
  isMobile?: boolean
  onValidatePromoCode: ValidatePromoCode
  onPersistData: (data: OrgFormData) => void
  onSubmit: (data: OrgFormData) => void
}

export function OrgFormStep(props: OrgFormStepProps) {
  const vm = useOrgFormStep(props)
  const dropdownProps = props.isMobile
    ? ({
        side: 'bottom',
        avoidCollisions: false,
        className: 'max-h-[var(--radix-select-content-available-height)]',
      } as const)
    : {}

  return (
    <form
      onSubmit={vm.handleSubmit}
      className="flex flex-col gap-4"
      data-testid="org-form"
      noValidate
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="org-form-name">Organization name</Label>
        <Input
          id="org-form-name"
          value={vm.orgName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setOrgName(e.target.value)}
          autoComplete="organization"
        />
        {vm.fieldErrors.orgName ? (
          <p role="alert" className="text-xs text-destructive">
            {vm.fieldErrors.orgName}
          </p>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="org-form-country">Country</Label>
          <Select
            value={vm.country}
            onValueChange={(value: string) => vm.setCountry(value as OrgFormCountry)}
          >
            <SelectTrigger id="org-form-country">
              <SelectValue placeholder="Select a country" />
            </SelectTrigger>
            <SelectContent {...dropdownProps}>
              <SelectItem value="US">United States</SelectItem>
              <SelectItem value="CA">Canada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="org-form-state">
            {vm.country === 'CA' ? 'Province' : 'State'}
          </Label>
          <Select value={vm.state} onValueChange={vm.setState}>
            <SelectTrigger id="org-form-state">
              <SelectValue
                placeholder={vm.country === 'CA' ? 'Select a province' : 'Select a state'}
              />
            </SelectTrigger>
            <SelectContent {...dropdownProps}>
              {vm.stateOptions.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {vm.fieldErrors.state ? (
            <p role="alert" className="text-xs text-destructive">
              {vm.fieldErrors.state}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="org-form-promo">Promo code</Label>
        <div className="flex items-start gap-2">
          <Input
            id="org-form-promo"
            value={vm.promoCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setPromoCode(e.target.value)}
            autoComplete="off"
            placeholder="Optional"
          />
          <Button
            type="button"
            variant="outline"
            onClick={vm.handleApplyPromo}
            disabled={vm.isApplyingPromo || vm.promoCode === '' || vm.promoApplied}
            data-testid="org-form-apply-promo"
          >
            {vm.isApplyingPromo ? 'Applying…' : vm.promoApplied ? 'Applied' : 'Apply'}
          </Button>
        </div>
        {vm.promoApplied && vm.appliedPromo ? (
          <p
            className="flex items-center gap-1.5 rounded-md bg-sage-soft px-2.5 py-1.5 text-xs font-medium text-sage"
            data-testid="org-form-promo-applied"
          >
            <CheckCircle2 aria-hidden className="size-3.5 shrink-0" />
            {promoAppliedMessage(vm.appliedPromo)}
          </p>
        ) : null}
        {vm.fieldErrors.promoCode ? (
          <p
            role="alert"
            className="flex items-center gap-1.5 rounded-md bg-destructive/10 px-2.5 py-1.5 text-xs font-medium text-destructive"
          >
            <XCircle aria-hidden className="size-3.5 shrink-0" />
            {vm.fieldErrors.promoCode}
          </p>
        ) : null}
      </div>
      {vm.submitError ? (
        <p role="alert" className="text-sm text-destructive">
          {vm.submitError}
        </p>
      ) : null}
      <Button
        type="submit"
        disabled={vm.isSubmitting}
        className="w-full"
        data-testid="org-form-submit"
      >
        {vm.isSubmitting ? 'Saving…' : 'Continue'}
      </Button>
    </form>
  )
}
